import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Re-fetch from server every 60s so we pick up admin revokes promptly.
const SERVER_POLL_MS = 60 * 1000;

/**
 * Small green chip in the global top-bar showing a live "Grace: MM:SS"
 * countdown when the current user is on an admin-granted grace period.
 * Hidden otherwise.
 *
 * - Polls /grace-periods/active every 60s for authoritative state.
 * - Ticks down client-side every 1s for a smooth display.
 * - Auto-hides at 0:00 (matches the server's no-buffer view).
 */
export function GraceCountdownChip() {
  const { user, token } = useAuth();
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [grace, setGrace] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => {
    if (!user || !token) return undefined;
    let cancelled = false;

    const fetchActive = async () => {
      try {
        const { data } = await axios.get(`${API}/loads/grace-periods/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (data?.active) {
          setGrace(data);
          setSecondsRemaining(Math.max(0, data.seconds_remaining || 0));
        } else {
          setGrace(null);
          setSecondsRemaining(0);
        }
      } catch {
        // silent — chip just hides
      }
    };
    fetchActive();
    const iv = setInterval(fetchActive, SERVER_POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, token]);

  // 1-second client-side tick so the display animates smoothly between server polls.
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (!grace || secondsRemaining <= 0) return undefined;
    tickRef.current = setInterval(() => {
      setSecondsRemaining(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [grace, secondsRemaining > 0]);   

  if (!grace || secondsRemaining <= 0) return null;

  const mins = Math.floor(secondsRemaining / 60);
  const secs = secondsRemaining % 60;
  const hours = Math.floor(mins / 60);
  const display = hours > 0
    ? `${hours}:${String(mins % 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    : `${mins}:${String(secs).padStart(2, '0')}`;

  // Tint shifts from green → amber when under 5 minutes left, to nudge urgency.
  const isUrgent = secondsRemaining < 5 * 60;
  const tone = isUrgent
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-emerald-50 border-emerald-300 text-emerald-900';

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 h-8 border rounded-md text-xs font-semibold ${tone}`}
      title={`Grace period granted by ${grace.granted_by_name || 'admin'}${grace.reason ? ` — ${grace.reason}` : ''}`}
      data-testid="grace-countdown-chip"
    >
      <ShieldCheck className="h-3.5 w-3.5" />
      <span>Grace: <span data-testid="grace-time-remaining">{display}</span></span>
    </div>
  );
}

export default GraceCountdownChip;
